// Types
import type { TPagination, TPaginated } from '~/misc/resource/types';
import type {
  TAutomatedEmailSentListQuery,
  TSentEmail,
} from '~/modules/opportunities/automated-email/types';
import type { TAutomatedEmailSentFilters } from '~/modules/opportunities/automated-email/sent/composables/use-sent-filters/types';

// Models
import { SentModel } from '~/modules/opportunities/automated-email/sent/models';

export const useSent = (filters: TAutomatedEmailSentFilters) => {
  const model: SentModel = new SentModel();

  const emails: Ref<TSentEmail[]> = ref<TSentEmail[]>([]);
  const pagination: Ref<TPagination | null> = ref<TPagination | null>(null);
  const selectedIds: Ref<Set<number>> = ref<Set<number>>(new Set());
  const isLoading: Ref<boolean> = ref<boolean>(false);

  const hasEmails: ComputedRef<boolean> = computed<boolean>(() => emails.value.length > 0);

  const selectedCount: ComputedRef<number> = computed<number>(() => selectedIds.value.size);

  const hasSelection: ComputedRef<boolean> = computed<boolean>(() => selectedIds.value.size > 0);

  const allSelected: ComputedRef<boolean> = computed<boolean>(
    () => emails.value.length > 0 && selectedIds.value.size === emails.value.length
  );

  const setEmails = (value: TSentEmail[]): void => {
    emails.value = value;
  };

  const setPagination = (value: TPagination | null): void => {
    pagination.value = value;
  };

  const setSelectedIds = (value: Set<number> | number[]): void => {
    selectedIds.value = value instanceof Set ? value : new Set(value);
  };

  const setIsLoading = (value: boolean): void => {
    isLoading.value = value;
  };

  const isSelected = (id: number): boolean => selectedIds.value.has(id);

  const toggleSelection = (id: number): void => {
    const next: Set<number> = new Set(selectedIds.value);

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    setSelectedIds(next);
  };

  const toggleSelectAll = (): void => {
    if (allSelected.value) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(emails.value.map((e: TSentEmail): number => e.id)));
    }
  };

  const clearSelection = (): void => {
    setSelectedIds(new Set());
  };

  const fetchSent = async (page: number = 1, options?: { silent?: boolean }): Promise<void> => {
    const silent: boolean = options?.silent === true;

    if (!silent) {
      setIsLoading(true);
    }

    try {
      const query: TAutomatedEmailSentListQuery | null = filters.toQueryParams(page);

      if (query === null) {
        setEmails([]);
        setPagination(null);

        return;
      }

      const response: TPaginated<TSentEmail> = await model.getSent(query);

      setEmails(response.data);
      setPagination(response.meta);
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  return {
    emails,
    pagination,
    isLoading,
    hasEmails,
    selectedCount,
    hasSelection,
    allSelected,
    isSelected,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    fetchSent,
    setSelectedIds,
  };
};
